/**
 * Новости админки — разметка 1:1 с legacy htdocs/core/templates/admin_news.tpl
 * (список: .infobox, .article/.title/.ctrl, кнопки .bt green/orange/red)
 * и admin_news_edit.tpl (редактор: form.add-form, .form-control.news-input, #news_text).
 *
 * Legacy держал список и редактор на разных URL (/admin/news и /admin/news/{id}, где 0 —
 * новая новость); здесь это один компонент с внутренним режимом — маршрут /admin/news
 * задан в App.tsx.
 *
 * API: GET/POST /api/admin/content/news, GET/PUT/DELETE /api/admin/content/news/{id}
 * (Handlers\Admin\Content). WYSIWYG summernote заменён на <textarea> в той же обёртке
 * (id=news_text, class=in_editor) — см. notes.
 */

import { useEffect, useState } from 'react'

import { api, ApiError } from '@/api/client'
import { HtmlEditor } from '@/components/HtmlEditor'
import type { Material } from '@/api/types'
import { useLegacyCss } from '@/lib/legacyAssets'
import { useApi } from '@/lib/useApi'

export function AdminNewsPage() {
  useLegacyCss('admin', 'newapproach') // те же стили, что подключал legacy admin.tpl

  const { data, reload } = useApi<Material[]>('/api/admin/content/news')

  /** null — список; 0 — новая новость; иначе id редактируемой (legacy /admin/news/{id}). */
  const [editId, setEditId] = useState<number | null>(null)
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [ok, setOk] = useState<string[]>([])
  const [error, setError] = useState<string[]>([])

  const { data: material } = useApi<Material>(
    editId !== null && editId > 0 ? `/api/admin/content/news/${editId}` : null,
  )

  useEffect(() => {
    if (material === null) return
    setTitle(material.title)
    setText(material.text)
  }, [material])

  const news = data ?? []

  const openEditor = (id: number): void => {
    setEditId(id)
    setTitle('')
    setText('')
    setOk([])
    setError([])
  }

  const backToList = (): void => {
    setEditId(null)
    setOk([])
    setError([])
  }

  /** legacy news-delete[id]. */
  const remove = async (id: number): Promise<void> => {
    try {
      const result = await api.delete<{ message: string }>(`/api/admin/content/news/${id}`)
      setOk([result.message])
      setError([])
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при удалении новости'])
    }
  }

  /** legacy news-submit: id=0 — добавление, иначе сохранение. */
  const submit = async (): Promise<void> => {
    if (editId === null) return
    try {
      const result =
        editId > 0
          ? await api.put<{ message: string }>(`/api/admin/content/news/${editId}`, { title, text })
          : await api.post<{ id: number; message: string }>('/api/admin/content/news', {
              title,
              text,
            })
      setOk([result.message])
      setError([])
      reload()
      if (editId === 0) setEditId(null) // legacy после добавления возвращал к списку
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при сохранении новости'])
      setOk([])
    }
  }

  const infobox = (
    <>
      {error.map((message, index) => (
        <p key={index} className="okke" style={{ color: '#ce4844', float: 'left' }}>
          {message}
        </p>
      ))}
      {ok.map((message, index) => (
        <p key={index} className="okke" style={{ float: 'left' }}>
          {message}
        </p>
      ))}
    </>
  )

  // ── Редактор новости (legacy admin_news_edit.tpl) ────────────────────────
  if (editId !== null) {
    return (
      <>
        <div className="infobox clearfix">
          {infobox}
          <div style={{ float: 'right' }}>
            <a
              className="bt"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                backToList()
              }}
            >
              К списку новостей
            </a>
          </div>
        </div>

        <form
          className="add-form"
          method="POST"
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <div
            className="form-control news-input"
            style={{ paddingBottom: '6px', display: 'table' }}
          >
            <span style={{ width: '120px', display: 'table-cell', color: '#999' }}>
              Название новости:&nbsp;
            </span>
            <input
              name="title"
              style={{ display: 'table-cell', border: 0, outline: 0, width: '100%' }}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <br />
          <HtmlEditor value={text} onChange={setText} />
          <div style={{ textAlign: 'center' }}>
            <input
              type="submit"
              name="news-submit"
              className={`bt ${title !== '' ? 'orange' : 'green'}`}
              value={editId > 0 ? 'Сохранить изменения' : 'Добавить новость'}
            />
          </div>
        </form>
      </>
    )
  }

  // ── Список новостей (legacy admin_news.tpl) ──────────────────────────────
  return (
    <>
      <div className="infobox clearfix">
        {infobox}
        <div style={{ float: 'right' }}>
          <a
            className="bt green"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              openEditor(0)
            }}
          >
            добавить новость
          </a>
        </div>
      </div>

      <form className="form-news" method="POST" onSubmit={(e) => e.preventDefault()}>
        {news.map((item) => (
          <div key={item.id}>
            <hr />
            <div className="article">
              <div className="title">
                {item.title}
                <br />
                <span style={{ fontSize: '10pt' }}>{item.date}</span>
              </div>
              <div className="ctrl">
                <a
                  href="#"
                  className="bt orange"
                  onClick={(e) => {
                    e.preventDefault()
                    openEditor(item.id)
                  }}
                >
                  редактировать
                </a>
                <input
                  type="submit"
                  name={`news-delete[${item.id}]`}
                  className="bt red"
                  value="удалить"
                  onClick={() => void remove(item.id)}
                />
              </div>
            </div>
          </div>
        ))}
      </form>
    </>
  )
}
